/**
 * Color scheme mapping utilities for Contentful background data
 */

import { BackgroundColorSchemes } from '@typings/contentful/background'

export interface ContentfulBackground {
  id: string
  name: BackgroundColorSchemes
  value: string
}

export type BackgroundType =
  | 'primary-default'
  | 'secondary-default'
  | 'secondary-dark'
  | 'accent-default'
  | 'tertiary-default'

export type ColorSchemeType = 'light' | 'dark'

export interface ColorSchemeResult {
  background: BackgroundType
  colorScheme: ColorSchemeType
}

/**
 * Maps Contentful background names to background and color scheme objects
 */
export const mapBackgroundToColorScheme = (background: ContentfulBackground): ColorSchemeResult => {
  // Map enumerated background schemes to background and color scheme objects
  const colorSchemeMap: Record<BackgroundColorSchemes, ColorSchemeResult> = {
    [BackgroundColorSchemes.PrimaryDefaultLight]: {
      background: 'primary-default',
      colorScheme: 'light',
    },
    [BackgroundColorSchemes.PrimaryDefaultDark]: {
      background: 'primary-default',
      colorScheme: 'dark',
    },
    [BackgroundColorSchemes.SecondaryDefaultLight]: {
      background: 'secondary-default',
      colorScheme: 'light',
    },
    [BackgroundColorSchemes.SecondaryDefaultDark]: {
      background: 'secondary-default',
      colorScheme: 'dark',
    },
    [BackgroundColorSchemes.SecondaryDarkLight]: {
      background: 'secondary-dark',
      colorScheme: 'light',
    },
    [BackgroundColorSchemes.SecondaryDarkDark]: {
      background: 'secondary-dark',
      colorScheme: 'dark',
    },
    [BackgroundColorSchemes.AccentDefaultLight]: {
      background: 'accent-default',
      colorScheme: 'light',
    },
    [BackgroundColorSchemes.AccentDefaultDark]: {
      background: 'accent-default',
      colorScheme: 'dark',
    },
    [BackgroundColorSchemes.TertiaryDefaultLight]: {
      background: 'tertiary-default',
      colorScheme: 'light',
    },
    [BackgroundColorSchemes.TertiaryDefaultDark]: {
      background: 'tertiary-default',
      colorScheme: 'dark',
    },
  }

  return colorSchemeMap[background.name] || { background: 'primary-default', colorScheme: 'light' }
}

/**
 * Maps Contentful background names to background and color scheme objects
 * This version handles the new Contentful background structure with id, name, and value
 */
export const mapContentfulBackgroundToColorScheme = (
  background: ContentfulBackground,
): ColorSchemeResult => {
  return mapBackgroundToColorScheme(background)
}

/**
 * Utility function to get color scheme from Contentful background
 * Handles both the new Contentful background structure and legacy Background type
 */
export const getBackgroundAndColorScheme = (
  background?: ContentfulBackground | any,
): ColorSchemeResult => {
  if (!background) {
    return { background: 'primary-default', colorScheme: 'light' }
  }

  // Check if it's the new Contentful background structure with proper typing
  if (background.name && Object.values(BackgroundColorSchemes).includes(background.name)) {
    return mapContentfulBackgroundToColorScheme(background as ContentfulBackground)
  }

  // Fallback for legacy background types or unknown structures
  return { background: 'primary-default', colorScheme: 'light' }
}

/**
 * Converts a ColorSchemeResult object to a combined string format
 * Useful for backward compatibility with existing code that expects string values
 */
export const colorSchemeResultToString = (result: ColorSchemeResult): string => {
  return `${result.background}-${result.colorScheme}`
}

/**
 * Utility function to get color scheme as string from Contentful background
 * Maintains backward compatibility for code that expects string return values
 */
export const getColorSchemeStringFromBackground = (
  background?: ContentfulBackground | any,
): string => {
  const result = getBackgroundAndColorScheme(background)
  return colorSchemeResultToString(result)
}
